import { Button, Dialog, Field, Input, Select } from '@wtfalch/design';
import { useState } from 'react';

import { isIsoDay, longDay, suggestDue, todayIso } from './dates.ts';
import { Who } from './Rows.tsx';
import {
  type FromKind,
  type NewTask,
  parseTitle,
  type Task,
  type TaskList,
  type TasksSource,
} from './source.ts';

export type MakeFrom = { kind: FromKind; sourceId: string; label: string; heading: string };

/**
 * "Make a task", from a message or a mail thread, in place. The words it was
 * made from suggest the day: "before Friday" fills Friday in and says so.
 */
export function MakeTask({
  source,
  lists,
  from,
  text,
  initialTitle,
  me,
  today = todayIso(),
  onCancel,
  onMade,
}: {
  source: TasksSource;
  lists: readonly TaskList[];
  from: MakeFrom;
  /** The words the task is made from; read for a day. */
  text: string;
  initialTitle: string;
  me: { id: string; name: string };
  today?: string;
  onCancel: () => void;
  onMade: (task: Task) => void;
}) {
  const suggestion = suggestDue(text, today);
  const [title, setTitle] = useState(initialTitle);
  const [dueOn, setDueOn] = useState(suggestion?.dueOn ?? '');
  const [listId, setListId] = useState(lists[0]?.id ?? 'mine');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const parsed = parseTitle(title);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    if (dueOn !== '' && !isIsoDay(dueOn)) {
      setError('That is not a date.');
      return;
    }
    setBusy(true);
    setError(null);
    const input: NewTask = {
      title: parsed.title,
      listId,
      dueOn: dueOn === '' ? null : dueOn,
      from: { kind: from.kind, sourceId: from.sourceId, label: from.label },
    };
    try {
      onMade(await source.create(input));
    } catch (err) {
      // Logged (agora#498) so the failure leaves a trace even though the
      // text on screen stays generic.
      console.error(err);
      setError('The task was not made.');
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Make a task"
      onCancel={onCancel}
      actions={
        <>
          <Button kind="ghost" onPress={onCancel}>
            Cancel
          </Button>
          <Button kind="primary" busy={busy} onPress={() => void submit()}>
            Make task
          </Button>
        </>
      }
    >
      <div className="tasks-make">
        <Field label="Title" error={error ?? undefined}>
          {(field) => (
            <Input block {...field} value={title} onChange={(e) => setTitle(e.target.value)} />
          )}
        </Field>
        <Field
          label="Due"
          hint={
            suggestion && dueOn === suggestion.dueOn
              ? `${longDay(suggestion.dueOn)} · Suggested from “${suggestion.phrase}”`
              : undefined
          }
        >
          {(field) => (
            <Input
              block
              type="date"
              {...field}
              value={dueOn}
              onChange={(e) => setDueOn(e.target.value)}
            />
          )}
        </Field>
        <div className="tasks-make-who">
          <span className="tasks-make-label">Who</span>
          <Who person={{ ...me, me: true }} size="sm" />
          {me.name}
          <span className="tasks-you">you</span>
        </div>
        <Field label="In">
          {(field) => (
            <Select block {...field} value={listId} onChange={(e) => setListId(e.target.value)}>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <p className="tasks-empty-line">{from.heading}</p>
      </div>
    </Dialog>
  );
}
